import { Disclosure, Transition } from '@headlessui/react';
import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ArchiveSwitch } from '@/modules/common/components/ArchiveSwitch.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { paymentsFilters } from '@/modules/issuer-dashboard/components/holder-tabs/PaymentsTab.tsx';
import { useIssuerAddActivityMenuOptions } from '@/modules/issuer-dashboard/hooks/useIssuerAddActivityMenuOptions.ts';
import { Button, CustomCheckbox, FilterItem, RoundBadge, Spinner, getSelectOptionByEnum } from 'ui';
import { FiltersIcon } from 'ui/icons';
import { PaymentDirection, PaymentStatus, ShareholderExtendResponse, SortedBy } from 'api';

const statusOptions = getSelectOptionByEnum<PaymentStatus>(PaymentStatus);
const directionOptions = getSelectOptionByEnum<PaymentDirection>(PaymentDirection);
const sortOptions = getSelectOptionByEnum<SortedBy>(SortedBy);

interface HolderPaymentsTabFiltersProps {
  filters: paymentsFilters;
  setFilters: Dispatch<SetStateAction<paymentsFilters>>;
  shareholder: ShareholderExtendResponse | null;
  isDeleted?: boolean;
  setIsDeleted?: Dispatch<SetStateAction<boolean | undefined>>;
  isFetching?: boolean;
}

const HolderPaymentsTabFilters = ({
  filters,
  setFilters,
  shareholder,
  isDeleted,
  setIsDeleted,
  isFetching = false,
}: HolderPaymentsTabFiltersProps) => {
  const { sorted_by, status, direction, requested_at_date_from, requested_at_date_to } = filters;
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const addActivityOptions = useIssuerAddActivityMenuOptions({
    registerId: Number(registerId),
    shareholderId: shareholder?.id,
  });

  const dateValue =
    requested_at_date_from && requested_at_date_to
      ? ([dayjs(requested_at_date_from), dayjs(requested_at_date_to)] as [Dayjs, Dayjs])
      : undefined;

  const appliedFiltersLength = Object.keys(filters).length;

  const handleToggleActionRequiredFirst = useCallback(
    (checked: boolean) => {
      setFilters(filters =>
        checked ? { ...filters, sorted_by: SortedBy.Status } : omit(filters, 'sorted_by'),
      );
    },
    [setFilters],
  );

  const handleChangeStatus = useCallback(
    (status: PaymentStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const handleChangeDirection = useCallback(
    (direction: PaymentDirection | null) => {
      setFilters(filters => (direction ? { ...filters, direction } : omit(filters, 'direction')));
    },
    [setFilters],
  );

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              requested_at_date_from: date[0].toISOString(),
              requested_at_date_to: date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['requested_at_date_from', 'requested_at_date_to']);
      });
    },
    [setFilters],
  );

  const handleChangeSortedBy = useCallback(
    (sorted_by: SortedBy | null) => {
      setFilters(filters => (sorted_by ? { ...filters, sorted_by } : omit(filters, 'sorted_by')));
    },
    [setFilters],
  );

  return (
    <Disclosure>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <CustomCheckbox
          label="Show Action required first"
          checked={sorted_by === SortedBy.Status}
          onChange={e => handleToggleActionRequiredFirst(e.target.checked)}
        />
        <div className="flex items-center gap-4 flex-wrap">
          {setIsDeleted && (
            <>
              {isFetching && <Spinner size="sm" className="m-0 mt-1" />}
              <ArchiveSwitch
                active={!!isDeleted}
                onChange={active => setIsDeleted(active || undefined)}
              />
            </>
          )}
          <Button
            className="w-[140px]"
            onClick={addActivityOptions.find(item => item.label === 'Payment')?.onClick}
          >
            Add Payment
          </Button>
          <Disclosure.Button>
            <div className="flex items-center border px-4 py-[0.5rem] rounded border-[rgba(23, 23, 23, 0.12)] gap-2">
              <img alt="filters" src={FiltersIcon} />
              <span className="text-sm">Filters</span>
              {!!appliedFiltersLength && <RoundBadge content={appliedFiltersLength} />}
            </div>
          </Disclosure.Button>
        </div>
      </div>
      <Transition
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <Disclosure.Panel static>
          <div className="flex flex-wrap gap-4 justify-end">
            <FilterItem<PaymentStatus>
              label="Status"
              type="select"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[170px]' }}
              value={status}
              options={statusOptions}
              onChange={handleChangeStatus}
            />
            <FilterItem<PaymentDirection>
              label="Direction"
              type="select"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[200px]' }}
              value={direction}
              options={directionOptions}
              onChange={handleChangeDirection}
            />
            <FilterItem
              label="Date range"
              type="date"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[200px]' }}
              onChange={handleChangeDate}
              value={dateValue}
            />
            <FilterItem
              label="Sorting"
              type="select"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[200px]' }}
              value={sorted_by}
              options={sortOptions}
              onChange={handleChangeSortedBy}
            />
          </div>
        </Disclosure.Panel>
      </Transition>
    </Disclosure>
  );
};

export default HolderPaymentsTabFilters;
